import React, { useState, useEffect } from 'react';

import { Space, Button, Select, Input, Form, Modal, InputNumber, Tooltip, Dropdown } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';
import { PlusOutlined, RollbackOutlined } from '@ant-design/icons';

import { exception, error } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import IBaseComponentProps from '@components/base-component-props';

import { IMarking } from '@entities/marking';
import { ICountryItem } from '@entities/country-item';
import { IPreloadingLine } from '@entities/preloading-line';
import { IConsignee } from '@entities/consignee';

import './preloading-items.css';

const dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc');
dayjs.extend(utc);

interface ILineMarking {
    lineId: string;
    consigneeId?: string;
    code?: string;
}

interface IPreloadingItemProps extends IBaseComponentProps {
    rootLine?: IPreloadingLine;
    api: NotificationInstance;
    onSave: () => void;
    onCancel: () => void;
}

const PreloadingItem = (props: IPreloadingItemProps) => {
    const { rootLine, api, onSave, onCancel, serverFetch } = props;

    const [form] = Form.useForm();
    const [newMarkingForm] = Form.useForm();

    const d = useAppDispatch();

    const [markings, setMarkings] = useState<Array<IMarking>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);
    const [consignees, setConsignees] = useState<Array<IConsignee>>([]);
    const [line, setLine] = useState<IPreloadingLine>();

    const [refreshMarkingsRequired, setRefreshMarkingsRequired] = useState<boolean>(true);
    const [assignMarkingOpen, setAssignMarkingOpen] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`countries/items`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`consignees`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountryItems(result[0][0]);
                setConsignees(result[0][1]);

                setLine(JSON.parse(JSON.stringify(rootLine)));
            });
        };

        fetchData();
        return () => {
            setLine(undefined);
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshMarkingsRequired) return;

        setRefreshMarkingsRequired(false);

        serverFetch('markings', { method: 'GET' })
            .then((data: any) => {
                setMarkings(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения маркировок', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshMarkingsRequired]);

    const onAddNewMarking = (entity: ILineMarking) => {
        if (!line) return;

        serverFetch(`markings`, { method: 'POST', bodyData: entity })
            .then((data: string) => {
                setRefreshMarkingsRequired(true);

                setTimeout(() => {
                    let copyLine: IPreloadingLine = { ...line };
                    copyLine.markingId = data;
                    form.setFieldValue('markingId', data);

                    setLine(copyLine);
                    setAssignMarkingOpen(false);

                    newMarkingForm.resetFields();
                }, 1000);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения маркировки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSaveLine = (data: IPreloadingLine) => {
        if (!line?.markingId) {
            error(api, `Маркировка "${line?.markingCode}" не найдена в системе`);
            return;
        }

        serverFetch(`preloading/line`, { method: line.id ? 'PUT' : 'POST', bodyData: line })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения груза', ex, () => d(userLoaded(undefined)));
                onSave();
            });
    };

    const onMenuClick = (e: any, id: string | undefined) => {
        switch (e.key) {
            case 'edit': {
                if (!line) break;

                let copyLine = { ...line };
                copyLine.newMarkingRequired = true;
                setLine(copyLine);

                break;
            }
        }
    };

    const actionItems = [
        {
            key: 'edit',
            label: 'Изменить',
        },
    ];

    return (
        <>
            <Modal
                destroyOnHidden={true}
                width={500}
                title={!line ? '' : line.id ? 'Изменить груз' : 'Добавить груз'}
                open={true}
                okText='ОК'
                onOk={() => {
                    form.submit();
                }}
                onCancel={() => onCancel()}
            >
                {line && (
                    <Form
                        colon={false}
                        labelCol={{ span: 6 }}
                        wrapperCol={{ span: 15 }}
                        style={{ marginTop: 20 }}
                        form={form}
                        onFinish={onSaveLine}
                    >
                        <Form.Item name='id' initialValue={line?.id} hidden>
                            <Input />
                        </Form.Item>

                        {line.markingId || line.newMarkingRequired || !line.markingCode ? (
                            <Form.Item label='Маркировка' required>
                                <Space.Compact style={{ width: 315 }}>
                                    <Form.Item
                                        wrapperCol={{ span: 24 }}
                                        style={{
                                            width: line.newMarkingRequired ? 293 : 325,
                                            minWidth: line.newMarkingRequired ? 293 : 325,
                                            marginBottom: 0,
                                        }}
                                        initialValue={line.markingId}
                                        required
                                        name='markingId'
                                        rules={[{ required: true, message: 'Укажите маркировку' }]}
                                    >
                                        <Select
                                            onChange={(value) => {
                                                const marking = markings.find((m) => m.id === value);

                                                let copyLine = { ...line };
                                                copyLine.markingId = marking?.id;
                                                copyLine.markingCode = marking?.code;

                                                setLine(copyLine);
                                            }}
                                            showSearch={{
                                                filterOption: (input, option) =>
                                                    (option?.label as string).toLowerCase().includes(input.toLowerCase()),

                                                filterSort: (a, b) =>
                                                    (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                            }}
                                            options={markings.map((m) => {
                                                return { value: m.id, label: `${m.code} / ${m.cityName}` };
                                            })}
                                        />
                                    </Form.Item>
                                    {line.newMarkingRequired && (
                                        <Button
                                            type='primary'
                                            icon={<RollbackOutlined />}
                                            onClick={() => {
                                                let copyLine = { ...line };
                                                copyLine.newMarkingRequired = false;
                                                copyLine.markingId = undefined;

                                                setLine(copyLine);
                                            }}
                                        />
                                    )}
                                </Space.Compact>
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label='Маркировка'
                                required
                                wrapperCol={{ span: 24 }}
                                validateStatus='error'
                                help='Маркировка не найдена'
                            >
                                <Space.Compact style={{ width: 505 }}>
                                    <Input style={{ color: 'var(--main-red)' }} readOnly value={line.markingCode} />
                                    <Dropdown.Button
                                        type='primary'
                                        menu={{ items: actionItems, onClick: (e) => onMenuClick(e, line.id) }}
                                        onClick={() => {
                                            newMarkingForm.setFieldValue('lineId', line.id);
                                            newMarkingForm.setFieldValue('code', line.markingCode);
                                            setAssignMarkingOpen(true);
                                        }}
                                    >
                                        <Tooltip title='Добавить в систему'>
                                            <PlusOutlined style={{ margin: -5 }} />
                                        </Tooltip>
                                    </Dropdown.Button>
                                </Space.Compact>
                            </Form.Item>
                        )}

                        <Form.Item
                            label='Продукт'
                            name='itemId'
                            initialValue={line.itemId}
                            rules={[{ required: true, message: 'Укажите продукт' }]}
                        >
                            <Select
                                style={{ width: 325 }}
                                onChange={(value) => {
                                    let copyLine = { ...line };
                                    copyLine.itemId = value;
                                    setLine(copyLine);
                                }}
                                showSearch={{
                                    filterOption: (input, option) =>
                                        (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                }}
                                options={countryItems
                                    .filter((i) => i.countryId == line.countryId)
                                    .map((i) => {
                                        return { value: i.itemId, label: i.itemName };
                                    })}
                            />
                        </Form.Item>
                        <Form.Item label='Количество' required>
                            <Space.Compact>
                                <Form.Item
                                    noStyle
                                    initialValue={line.qty}
                                    name='qty'
                                    rules={[{ required: true, message: 'Укажите количество' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        onChange={(value: number | null) => {
                                            setLine({ ...line, qty: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>шт</Space.Addon>
                            </Space.Compact>
                        </Form.Item>

                        <Form.Item label='Брутто' required>
                            <Space.Compact>
                                <Form.Item
                                    noStyle
                                    initialValue={line.grossWeight}
                                    name='grossWeight'
                                    rules={[{ required: line.countryCode !== 'NL', message: 'Укажите вес брутто' }]}
                                >
                                    <InputNumber
                                        decimalSeparator=','
                                        min={0}
                                        stringMode
                                        step={1}
                                        onChange={(value: number | null) => {
                                            setLine({ ...line, grossWeight: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>кг</Space.Addon>
                            </Space.Compact>
                        </Form.Item>

                        <Form.Item label='Объемный' required>
                            <Space.Compact>
                                <Form.Item
                                    noStyle
                                    initialValue={line.volumeWeight}
                                    name='volumeWeight'
                                    rules={[{ required: line.countryCode !== 'NL', message: 'Укажите объемный вес' }]}
                                >
                                    <InputNumber
                                        decimalSeparator=','
                                        min={0}
                                        stringMode
                                        step={1}
                                        onChange={(value: number | null) => {
                                            setLine({ ...line, volumeWeight: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>кг</Space.Addon>
                            </Space.Compact>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <Modal
                destroyOnHidden={true}
                width={500}
                title='Привязать маркировку'
                open={assignMarkingOpen}
                okText='ОК'
                onOk={() => {
                    newMarkingForm.submit();
                }}
                onCancel={() => {
                    newMarkingForm.resetFields();
                    setAssignMarkingOpen(false);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    style={{ marginTop: 20 }}
                    form={newMarkingForm}
                    onFinish={onAddNewMarking}
                >
                    <Form.Item hidden name='lineId'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item label='Маркировка' name='code'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{ span: 8 }}
                        name='consigneeId'
                        label='ID'
                        rules={[{ required: true, message: 'Укажите ID грузополучателя' }]}
                    >
                        <Select
                            showSearch={{
                                filterOption: (input, option) => (`${option?.label}` as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label && b?.label && a?.label > b?.label ? 1 : -1),
                            }}
                            options={consignees.map((a) => {
                                return {
                                    value: a.id,
                                    label: a.code,
                                };
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default PreloadingItem;
